/* eslint-disable @typescript-eslint/no-misused-promises */
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { addUserMetadata, sendErrorEvent } from '@Analytics';
import { bopsApi } from '@Network';
import { AppDispatch, authorizationOperations, RootState, setAccessTokenSilently } from '@Store';
import config from '../../config';
import FullPageLoading from '../FullPageLoading/fullPageLoading';

const LogycaAuthenticationGuard = ({
  component,
  token,
  refreshToken,
  email,
  setToken,
  clearToken,
  persistedToken,
  setPermissions,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const ReturnComponent = component;
  const validToken = token || persistedToken || config.REACT_APP_DEV_TOKEN;
  const validRefresh = refreshToken || config.REACT_APP_DEV_REFRESH;
  const validEmail = email || config.REACT_APP_DEV_EMAIL;

  useEffect(() => {
    if (validToken) {
      setAccessTokenSilently(() => {
        return validToken;
      });
    }

    const logoutUser = () => {
      clearToken();
      window.location.href = config.REACT_APP_LOGYCA_LOGIN_URL;
    };

    const authenticateWithSSO = async () => {
      try {
        const response = await bopsApi.validateUser();
        if (response.clientName) {
          setToken({ token: validToken, refreshToken: validRefresh });
          setPermissions(response.permissions || []);

          addUserMetadata({
            clientName: response.clientName,
            email: validEmail,
          });
          setIsLoading(false);
        } else {
          logoutUser();
        }
      } catch (err) {
        logoutUser();
      }
    };

    authenticateWithSSO();
  }, []);

  useEffect(() => {
    const refreshTokenCall = async () => {
      try {
        const response = await bopsApi.refreshUser(refreshToken);
        setToken({ token: response.token, refreshToken: response.refreshToken });
      } catch (error) {
        sendErrorEvent(error);
        clearToken();
      }
    };

    const interval = setInterval(refreshTokenCall, 45 * 60 * 1000); // every 45 minutes

    return () => clearInterval(interval);
  }, [refreshToken]);

  if (isLoading || !validToken) return <FullPageLoading />;

  return <ReturnComponent />;
};

const mapStateToProps = (state: RootState) => {
  const { authorization } = state;
  const { token } = authorization;

  return { persistedToken: token };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  const { setToken, clearToken, setPermissions } = authorizationOperations;

  return {
    setToken: ({ token, refreshToken }: { token: string; refreshToken: string }) =>
      dispatch(setToken({ token, refreshToken })),
    clearToken: () => dispatch(clearToken()),
    setPermissions: (permissions: string[]) => dispatch(setPermissions({ permissions })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LogycaAuthenticationGuard);
